import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators } from 'redux';
import { defaultIndustrySettings } from '../../../../../constants';
import {
  isBusinessAdmin,
  responsibleManagerAdded
} from '../../../../../services';
import {
  businessActions,
  businessCpdCategorySelector,
  businessMainIndustryRole,
  businessSelector
} from '../../../../../state/modules/business';
import {
  currentUserIndustry,
  currentUserMainIndustryRole,
  currentUserSelector,
  userActions
} from '../../../../../state/modules/user';
import { ConfirmationModal } from '../../../../components';
import Loading from '../../../loading';
import { CpdSettings } from '../components';
import TrainingPlanEdit from '../components/training-plan-edit';
import {
  filterNonLegacy,
  filterNonRmGoalCategory
} from '../../../../../common/cpd-category-filter';

export const UserTrainingPlan = compose(
  connect(
    (state) => {
      const cpdCategoryIdsWithRmGoal =
        state.systemStatus.profile.cpdCategoryGoals
          .filter(({ progressGoalName }) => progressGoalName === 'RM CPD')
          .map(({ cpdCategoryId }) => cpdCategoryId);
      return {
        business: businessSelector(state),
        currentUser: currentUserSelector(state),
        userIndustryType: fp.flow(
          currentUserIndustry,
          fp.first,
          fp.get('industryType')
        )(state),
        industryRole: currentUserMainIndustryRole(state),
        industryCpdCategorys: fp.flow(
          filterNonLegacy,
          filterNonRmGoalCategory(cpdCategoryIdsWithRmGoal)
        )(state.industry.cpdCategorys),
        isFetchingIndustry: state.industry.isFetching,
        isUpdating: state.user.isUpdating,
        isFetchingData: state.user.isFetching,
        cpdCategorys: state.user.cpdCategories,
        setTrainingPlanSuccess: state.user.setTrainingPlanSuccess
      };
    },
    (dispatch) => ({
      saveUserCpdCategorys: bindActionCreators(
        userActions.user.setCpdCategorys,
        dispatch
      )
    })
  ),
  branch(({ industryRole }) => !industryRole, renderComponent(Loading)),
  withProps(
    ({
      industryRole,
      isFetchingIndustry,
      isFetchingData,
      business,
      currentUser
    }) => ({
      planOwner: 'yourself',
      planLocked:
        !isBusinessAdmin(currentUser) &&
        business !== undefined &&
        business !== null,
      isLoading: isFetchingData || isFetchingIndustry,
      industry: {
        ...defaultIndustrySettings,
        ...industryRole
      },
      cpdCategoryTransform: (cpdCategory) => ({
        ...cpdCategory,
        userId: 0 // Dummy user id so the server accepts the JSON.
      }),
      headings: {
        cpdCategorys: [
          <p key='heading'>
            <strong>Your CPD categories</strong>
          </p>,
          <p key='copy'>
            Specify the knowledge areas you are permitted to provide general and
            personal advice for.
          </p>
        ],
        cpdDate: [
          <p key='heading'>
            <strong>Enter the last day of the CPD Year</strong>
          </p>,
          <p key='copy'>
            Your CPD anniversary date is the annual year in which your CPD track
            against. Our default CPD year runs from July 1 - June 30. You can
            change this at any time.
          </p>
        ],
        cpdPoints: [
          <p key='heading'>
            <strong>How many CPD hours do you need to earn in a year?</strong>
          </p>,
          <p key='copy'>
            Specify the total number of CDP hours you are required to complete
            each year.
          </p>
        ],
        cpdAllocation: [
          <p key='heading'>
            <strong>CPD allocation</strong>
          </p>,
          <p key='copy'>
            Specify the number of CPD hours you are required to meet for each
            CPD category you provide services on.
          </p>
        ]
      }
    })
  ),
  withHandlers({
    saveCpdCategorys:
      ({ saveUserCpdCategorys }) =>
      ({ industry, ...payload }) =>
        saveUserCpdCategorys({
          ...payload,
          industry
        })
  }),
  withStateHandlers(
    {
      isSaved: false,
      hasFailed: false
    },
    {
      showSavedBadge: () => () => ({ isSaved: true }),
      hideSavedBadge: () => () => ({ isSaved: false }),
      showFailedBadge: () => () => ({ hasFailed: true }),
      hideFailedBadge: () => () => ({ hasFailed: false })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        setTrainingPlanSuccess,
        showSavedBadge,
        hideSavedBadge,
        showFailedBadge
      } = this.props;
      if (setTrainingPlanSuccess && !prevProps.setTrainingPlanSuccess) {
        if (setTrainingPlanSuccess.success) {
          showSavedBadge();
          setTimeout(() => hideSavedBadge(), 1000);
        } else {
          showFailedBadge();
        }
      }
    }
  })
)(CpdSettings);

const PostupdateInfoModal = ({ show, cancel }) => {
  return (
    <Modal show={show} onHide={cancel}>
      <Modal.Header>
        <Modal.Title componentClass='h2' className='text-center'>
          Business Training Plan Updated
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-center bottom-gap'>
        This training plan has been saved and will apply to all future users
        added to your company.
      </Modal.Body>
      <Modal.Footer className='text-center confirmation-footer'>
        <Row>
          <Col xs={12}>
            <Button onClick={cancel}>Got it</Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export const BusinessTrainingPlan = compose(
  connect(
    (state) => {
      const cpdCategoryIdsWithRmGoal =
        state.systemStatus.profile.cpdCategoryGoals
          .filter(({ progressGoalName }) => progressGoalName === 'RM CPD')
          .map(({ cpdCategoryId }) => cpdCategoryId);
      return {
        business: businessSelector(state),
        profile: state.business.profile,
        industryRole: businessMainIndustryRole(state),
        isFetchingIndustry: state.industry.isFetching,
        isUpdating: state.business.isUpdating,
        isFetchingData: state.business.isFetching,
        cpdCategorys: businessCpdCategorySelector(state),
        currentUser: currentUserSelector(state),
        currentUserIndustry: fp.first(currentUserIndustry(state)),
        businessTaskStack: state.business.taskStack,
        isUpdatingBusiness: state.business.isUpdating,
        isUpdatingBusinessUser: state.business.user.isUpdating,
        industryCpdCategorys: fp.flow(
          filterNonLegacy,
          filterNonRmGoalCategory(cpdCategoryIdsWithRmGoal)
        )(state.industry.cpdCategorys),
        setTrainingPlanSuccess: state.business.setTrainingPlanSuccess
      };
    },
    (dispatch) => ({
      getBusinessCpdCategorys: bindActionCreators(
        businessActions.business.getCpdCategorys,
        dispatch
      ),
      getBusinessIndustryRoles: bindActionCreators(
        businessActions.business.getIndustryRoles,
        dispatch
      ),
      getBusinessProfile: bindActionCreators(
        businessActions.business.profile,
        dispatch
      ),
      setIndustryRole: bindActionCreators(
        businessActions.business.setIndustryRole,
        dispatch
      ),
      setTrainingPlan: bindActionCreators(
        businessActions.business.setTrainingPlan,
        dispatch
      ),
      setProgressGoals: bindActionCreators(
        businessActions.business.setProgressGoals,
        dispatch
      ),
      saveBusinessCpdCategorys: bindActionCreators(
        businessActions.business.setCpdCategorys,
        dispatch
      )
    })
  ),
  withStateHandlers(
    {
      showUpdateVerificationModal: false,
      isApplyingToAll: false,
      isSaved: false,
      hasFailed: false
    },
    {
      showVerificationConfirmation: () => () => ({
        showUpdateVerificationModal: true
      }),
      hideVerificationConfirmation: () => () => ({
        showUpdateVerificationModal: false
      }),
      setApplyingToAll: () => (isApplyingToAll) => ({
        isApplyingToAll
      }),
      showSavedBadge: () => () => ({ isSaved: true }),
      hideSavedBadge: () => () => ({ isSaved: false }),
      showFailedBadge: () => () => ({ hasFailed: true }),
      hideFailedBadge: () => () => ({ hasFailed: false })
    }
  ),
  lifecycle({
    componentDidMount() {
      const {
        business: { id: businessId },
        getBusinessCpdCategorys,
        getBusinessIndustryRoles,
        getBusinessProfile
      } = this.props;

      getBusinessProfile(businessId);
      getBusinessCpdCategorys(businessId);
      getBusinessIndustryRoles(businessId);
    },
    componentDidUpdate(prevProps) {
      const {
        setTrainingPlanSuccess,
        showVerificationConfirmation,
        isApplyingToAll,
        setApplyingToAll,
        showSavedBadge,
        hideSavedBadge,
        showFailedBadge
      } = this.props;
      if (setTrainingPlanSuccess && !prevProps.setTrainingPlanSuccess) {
        if (setTrainingPlanSuccess.success) {
          showSavedBadge();
          setTimeout(() => hideSavedBadge(), 1000);

          if (!isApplyingToAll) {
            showVerificationConfirmation();
          } else {
            setApplyingToAll(false);
          }
        } else {
          showFailedBadge();
        }
      }
    }
  }),
  withProps(({ industryRole, isFetchingIndustry, isFetchingData }) => ({
    isLoading: isFetchingData || isFetchingIndustry,
    industry: {
      ...defaultIndustrySettings,
      ...industryRole
    }
  })),
  branch(
    ({ industryRole, isLoading }) => !industryRole || isLoading,
    renderComponent(Loading)
  ),
  withProps(({ business, industryRole, cpdCategorys }) => ({
    businessTrainingPlan: {
      cpdCategorys,
      industry: industryRole
    }
  })),
  withHandlers({
    getData:
      ({ business, getBusinessCpdCategorys, getBusinessIndustryRoles }) =>
      () => {
        getBusinessCpdCategorys(business.id);
        getBusinessIndustryRoles(business.id);
      },
    applyTrainingPlanToUsers:
      ({
        saveBusinessCpdCategorys,
        business,
        industryRole,
        cpdCategorys,
        currentUserIndustry,
        currentUser,
        setApplyingToAll
      }) =>
      () => {
        setApplyingToAll(true);
        saveBusinessCpdCategorys({
          applyToAllMembers: true,
          cpdCategorys,
          businessId: business.id,
          industry: industryRole,
          industryId: currentUserIndustry.id,
          userId: currentUser.id,
          noRedirect: true
        });
      }
  }),
  withStateHandlers(
    {
      trainingPlanUpdateConfirmation: {
        show: false,
        data: undefined
      }
    },
    {
      showConfirmation: () => (data) => ({
        trainingPlanUpdateConfirmation: {
          show: true,
          data
        }
      }),
      hideConfirmation: () => () => ({
        trainingPlanUpdateConfirmation: {
          show: false,
          data: undefined
        }
      })
    }
  ),
  withProps(({ applyTrainingPlanToUsers, isApplyingToAll }) => ({
    externalControls: ({ trainingPlans }) => {
      const isEditing = fp.any(fp.get('isEditing'))(trainingPlans);
      const isUpdating = fp.any(fp.get('isUpdating'))(trainingPlans);
      const disabled = isEditing || isUpdating;
      return (
        <Button
          className='apply-to-all-button'
          disabled={disabled}
          onClick={applyTrainingPlanToUsers}
        >
          {isApplyingToAll ? (
            <Spinner name='circle' className='spinner' fadeIn='none' />
          ) : (
            'Apply to all'
          )}
        </Button>
      );
    }
  }))
)(
  ({
    businessTrainingPlan,
    isUpdatingBusiness,
    isSaved,
    hasFailed,
    industryCpdCategorys,
    setTrainingPlan,
    setIndustryRole,
    setProgressGoals,
    showConfirmation,
    hideConfirmation,
    trainingPlanUpdateConfirmation,
    business,
    showUpdateVerificationModal,
    hideVerificationConfirmation,
    externalControls,
    hideFailedBadge,
    profile
  }) => (
    <Grid>
      <form>
        <Row className='no-gutter'>
          <Col xs={12}>
            <TrainingPlanEdit
              externalControls={externalControls}
              entityTrainingPlans={[
                {
                  onSave: ({
                    entity,
                    trainingPlan,
                    industryId,
                    industryEntity,
                    goals,
                    callback
                  }) => {
                    const trainingPlanWithEntityId = fp.map((plan) => ({
                      ...plan,
                      businessId: entity.id
                    }))(trainingPlan);
                    if (
                      responsibleManagerAdded(
                        trainingPlanWithEntityId,
                        businessTrainingPlan.cpdCategorys,
                        industryCpdCategorys
                      )
                    ) {
                      showConfirmation({
                        industryEntity,
                        trainingPlan: trainingPlanWithEntityId,
                        industryId,
                        goals,
                        callback
                      });
                    } else {
                      setTrainingPlan({
                        industryId: industryId,
                        industry: industryEntity,
                        cpdCategorys: trainingPlanWithEntityId
                      });
                      setIndustryRole({
                        industry: industryEntity
                      });
                      setProgressGoals(entity.id, goals);
                      callback();
                    }
                  },
                  isUpdating: isUpdatingBusiness,
                  isSaved,
                  hasFailed,
                  entity: business,
                  industryEntity: businessTrainingPlan.industry,
                  goals: fp.flow(
                    fp.get('trainingPlans'),
                    fp.flatMap('goals')
                  )(profile),
                  trainingPlan: businessTrainingPlan.cpdCategorys
                }
              ]}
              entityTitle='Company Plan'
              hideFailedBadge={hideFailedBadge}
              industryCpdCategorys={industryCpdCategorys}
            />
          </Col>
        </Row>

        <Row className='no-gutter submit-row text-center'>
          <Col xs={12} sm={7} smOffset={5}>
            <ConfirmationModal
              id='confirm-update-training-plan'
              show={trainingPlanUpdateConfirmation.show}
              actions={[
                {
                  onConfirmAction: () => {
                    const {
                      industryEntity,
                      trainingPlan,
                      industryId,
                      goals,
                      callback
                    } = trainingPlanUpdateConfirmation.data;
                    setTrainingPlan({
                      industryId: industryId,
                      industry: industryEntity,
                      cpdCategorys: trainingPlan
                    });
                    setIndustryRole({ industryEntity });
                    setProgressGoals(goals);
                    callback();
                    hideConfirmation();
                  }
                }
              ]}
              cancel={hideConfirmation}
              message={
                <span>
                  You’re about to assign{' '}
                  <strong>{he.decode(business.name)}</strong> Responsible
                  Manager content as a part of its CPD training plan. This
                  content should only be completed by RG105 representatives. Do
                  you wish to continue?
                </span>
              }
              headingText={<span>Update Training Plan</span>}
              confirmLabel={'Yes'}
            />
          </Col>
        </Row>

        <Row className='no-gutter submit-row text-center'>
          <Col xs={12} sm={7} smOffset={5}>
            <PostupdateInfoModal
              show={showUpdateVerificationModal}
              cancel={hideVerificationConfirmation}
            />
          </Col>
        </Row>
      </form>
    </Grid>
  )
);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/training-plan.js